import tokenService from '@/data/api/tokenService';
import authTokenHandler from '@/utils/authTokenHandler';
import axiosInstance from '@/data/api/axiosInstance';
import tokenRequestQueue from '@/data/request/tokenRequestQueue';

/**
 * A "success" response interceptor:
 * Simply passes the response through unchanged.
 */
export const responseInterceptor = (response) => {
  return response;
};

/**
 * An "error" response interceptor to handle 401 errors
 * and token refresh. It uses the tokenRequestQueue to manage
 * concurrent 401 responses.
 */
export const responseInterceptorError = (error) => {
  const { response } = error;
  const originalRequest = error.config;

  // If there's no response or the status isn't 401, just reject the promise
  if (
    !response ||
    response.status !== 401 ||
    authTokenHandler.shouldBypassTokenHandling(originalRequest?.url)
  ) {
    return Promise.reject(error);
  }

  // We got a 401, so check if we're already refreshing
  if (!tokenRequestQueue.isRefreshing) {
    // Not refreshing yet, so start now
    tokenRequestQueue.isRefreshing = true;

    return tokenService
      .refreshToken(axiosInstance)
      .then(() => authTokenHandler.retrieveToken())
      .then((tokenData) => {
        const newAccessToken = tokenData?.access_token || null;

        // Notify all queued requests (if any) that refresh succeeded
        tokenRequestQueue.processQueue(null, newAccessToken);

        // If we got a new token, attach it to the original request
        if (newAccessToken) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        }

        // Retry the original request
        return axiosInstance(originalRequest);
      })
      .catch((refreshError) => {
        // Refresh failed: reject all queued requests
        tokenRequestQueue.processQueue(refreshError, null);

        // Optionally, force logout or redirect to login here
        return Promise.reject(refreshError);
      })
      .finally(() => {
        // Unlock for future refresh attempts
        tokenRequestQueue.isRefreshing = false;
      });
  }

  // Otherwise, if we're already refreshing, queue this request
  return new Promise((resolve, reject) => {
    tokenRequestQueue.enqueue((newToken) => {
      if (newToken) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
      }
      resolve(axiosInstance(originalRequest));
    }, reject);
  });
};
